/**
 * Admin install-logs RPC definitions (CLA-217).
 *
 * Two endpoints:
 *
 *   - `admin/installs/logs.list` — paginated historical read backed by
 *     `installation_logs` in Postgres. Filterable by `source`, `level`,
 *     and an ISO timestamp cursor (`before`/`after`).
 *
 *   - `admin/installs/logs.stream` — streaming RPC (`Rpc.stream`) that
 *     subscribes the caller to the install's Redis topic via
 *     {@link InstallLogHub}. The stream tears down when the caller
 *     disconnects.
 *
 * Both endpoints are admin-only (see {@link AdminMiddleware}).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { InstallationLogLevel, InstallationLogSource } from "@fm/db/models/InstallationLog";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Constants
// ============================================================================

/**
 * Default page size for `admin/installs/logs.list`. Keeps round-trip
 * latency snappy for the common "show me the latest N" admin view.
 *
 * @since 1.0.0
 */
export const DEFAULT_LOGS_LIST_LIMIT = 100;

/**
 * Server-side hard cap on the page size — mirrors the underlying
 * accessor's clamp so a misbehaving client cannot pull unbounded rows.
 *
 * @since 1.0.0
 */
export const MAX_LOGS_LIST_LIMIT = 500;

// ============================================================================
// Wire schemas
// ============================================================================

/**
 * JSON-safe projection of a single install log line.
 *
 * `meta` is opaque jsonb. `ts` is the producer-supplied timestamp;
 * `createdAt` is when the row landed in Postgres.
 *
 * @since 1.0.0
 */
export const InstallLogLine = Schema.Struct({
  id: Schema.String,
  installationId: Schema.String,
  source: InstallationLogSource,
  level: InstallationLogLevel,
  message: Schema.String,
  meta: Schema.NullOr(Schema.Unknown),
  ts: Schema.DateTimeUtc,
  createdAt: Schema.String,
});
export type InstallLogLine = Schema.Schema.Type<typeof InstallLogLine>;

/**
 * Input for the historical list RPC. `limit` is clamped server-side
 * to {@link MAX_LOGS_LIST_LIMIT}; `before` and `after` are ISO-8601
 * timestamp cursors and may be combined to scope a window.
 *
 * @since 1.0.0
 */
export const InstallLogsListInput = Schema.Struct({
  installationId: Schema.String,
  limit: Schema.optional(Schema.Int),
  before: Schema.optional(Schema.String),
  after: Schema.optional(Schema.String),
  source: Schema.optional(InstallationLogSource),
  level: Schema.optional(InstallationLogLevel),
});
export type InstallLogsListInput = Schema.Schema.Type<typeof InstallLogsListInput>;

/**
 * Response for the historical list RPC. `nextCursor` is an ISO-8601
 * `ts` value that the caller can pass back as `before` to walk older
 * pages; `null` when the page exhausted history.
 *
 * @since 1.0.0
 */
export const InstallLogsListResponse = Schema.Struct({
  items: Schema.Array(InstallLogLine),
  nextCursor: Schema.NullOr(Schema.String),
});
export type InstallLogsListResponse = Schema.Schema.Type<typeof InstallLogsListResponse>;

/**
 * Input for the streaming RPC. The caller subscribes to live tail
 * events for a single install; disconnect tears down the Redis
 * subscription.
 *
 * @since 1.0.0
 */
export const InstallLogsStreamInput = Schema.Struct({
  installationId: Schema.String,
});
export type InstallLogsStreamInput = Schema.Schema.Type<typeof InstallLogsStreamInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Install-Logs RPC group. Tags are namespaced under
 * `admin/installs/`.
 *
 * @since 1.0.0
 */
export class InstallLogsRpcs extends RpcGroup.make(
  Rpc.make("logs.list", {
    payload: InstallLogsListInput,
    success: InstallLogsListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("logs.stream", {
    payload: InstallLogsStreamInput,
    success: InstallLogLine,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
    stream: true,
  }).middleware(AdminMiddleware),
).prefix("admin/installs/") {}
